'use client';

import { useState, useEffect, useCallback } from 'react';
import Icon from '@/components/ui/AppIcon';

// ─── Types ────────────────────────────────────────────────────────────────────

interface ProviderStatus {
  id: string;
  name: string;
  providerId: string;
  status: 'connected' | 'error' | 'rate_limited' | 'unconfigured';
  latency: number | null;
  lastChecked: string | null;
  fallbackOrder: number;
  isConfigured: boolean;
}

const PROVIDER_META: Record<string, { name: string; fallbackOrder: number; color: string }> = {
  polygon:      { name: 'Polygon.io',    fallbackOrder: 1, color: '#0EA5E9' },
  twelve:       { name: 'Twelve Data',   fallbackOrder: 2, color: '#10B981' },
  intrinio:     { name: 'Intrinio',      fallbackOrder: 3, color: '#8B5CF6' },
  alphavantage: { name: 'Alpha Vantage', fallbackOrder: 4, color: '#F59E0B' },
  newsapi:      { name: 'NewsAPI',       fallbackOrder: 5, color: '#EC4899' },
  fred:         { name: 'FRED API',      fallbackOrder: 6, color: '#14B8A6' },
};

const STATUS_CONFIG = {
  connected:    { label: 'Live',         dot: 'bg-emerald-500', text: 'text-emerald-400', badge: 'bg-emerald-500/10 border-emerald-500/30' },
  error:        { label: 'Error',        dot: 'bg-red-500',     text: 'text-red-400',     badge: 'bg-red-500/10 border-red-500/30' },
  rate_limited: { label: 'Rate Limited', dot: 'bg-amber-500',   text: 'text-amber-400',   badge: 'bg-amber-500/10 border-amber-500/30' },
  unconfigured: { label: 'Not Set',      dot: 'bg-slate-600',   text: 'text-slate-500',   badge: 'bg-slate-800 border-slate-700' },
};

// ─── Component ────────────────────────────────────────────────────────────────

interface ProviderStatusPanelProps {
  className?: string;
}

export default function ProviderStatusPanel({ className = '' }: ProviderStatusPanelProps) {
  const [providers, setProviders] = useState<ProviderStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastPoll, setLastPoll] = useState<string>('');
  const [fallbackAlert, setFallbackAlert] = useState<string | null>(null);
  const [collapsed, setCollapsed] = useState(false);

  const pollProviders = useCallback(async () => {
    try {
      const res = await fetch('/api/settings?section=api_keys');
      if (!res.ok) return;
      const json = await res.json();
      // Settings route returns `apiKeys` (camelCase)
      const apiKeys: any[] = json.apiKeys || [];

      const statuses: ProviderStatus[] = Object.entries(PROVIDER_META).map(([pid, meta]) => {
        const key = apiKeys.find((k: any) => k.provider_id === pid);
        return {
          id: pid,
          name: meta.name,
          providerId: pid,
          status: key ? (key.status as ProviderStatus['status']) : 'unconfigured',
          latency: key?.latency_ms ?? null,
          lastChecked: key?.last_tested_at ?? null,
          fallbackOrder: meta.fallbackOrder,
          isConfigured: !!(key?.hasKey),
        };
      });

      // Sort by fallback order
      statuses.sort((a, b) => a.fallbackOrder - b.fallbackOrder);
      setProviders(statuses);

      // Check for fallback alerts
      const primaryProviders = statuses.filter(p => p.fallbackOrder <= 4 && p.isConfigured);
      const failedPrimary = primaryProviders.filter(p => p.status === 'error' || p.status === 'rate_limited');
      if (failedPrimary.length > 0) {
        const names = failedPrimary.map(p => p.name).join(', ');
        setFallbackAlert(`Fallback active: ${names} unavailable — using next provider in chain`);
      } else {
        setFallbackAlert(null);
      }

      setLastPoll(new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false }));
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    pollProviders();
    const id = setInterval(pollProviders, 30000); // poll every 30s
    return () => clearInterval(id);
  }, [pollProviders]);

  const connectedCount = providers.filter(p => p.status === 'connected').length;
  const errorCount = providers.filter(p => p.status === 'error' || p.status === 'rate_limited').length;

  if (loading) {
    return (
      <div className={`bg-[#1E293B] border border-[#334155] rounded-xl p-4 ${className}`}>
        <div className="flex items-center gap-2 mb-3">
          <div className="w-2 h-2 rounded-full bg-slate-600 animate-pulse" />
          <span className="text-xs text-slate-500 font-mono">Checking providers…</span>
        </div>
        <div className="space-y-2">
          {[1, 2, 3, 4, 5, 6].map(i => (
            <div key={i} className="h-8 bg-[#0F172A] rounded animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-[#1E293B] border border-[#334155] rounded-xl overflow-hidden ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-[#334155]">
        <div className="flex items-center gap-2">
          <div className={`w-2 h-2 rounded-full ${errorCount > 0 ? 'bg-amber-500 animate-pulse' : 'bg-emerald-500 animate-pulse'}`} />
          <span className="text-xs font-semibold text-slate-300 font-mono uppercase tracking-wide">Data Provider Status</span>
          <span className="text-xs text-slate-600 font-mono">· polls every 30s</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs font-mono text-slate-500">{connectedCount}/{providers.length} live</span>
          <button
            onClick={() => setCollapsed(v => !v)}
            className="text-slate-600 hover:text-slate-400 transition-colors"
            title={collapsed ? 'Expand' : 'Collapse'}
          >
            <Icon name={collapsed ? 'ChevronDownIcon' : 'ChevronUpIcon'} size={14} variant="outline" />
          </button>
        </div>
      </div>

      {!collapsed && (
        <>
          {/* Fallback alert */}
          {fallbackAlert && (
            <div className="mx-3 mt-3 flex items-start gap-2 px-3 py-2 bg-amber-500/10 border border-amber-500/30 rounded-lg">
              <Icon name="ExclamationTriangleIcon" size={13} variant="solid" className="text-amber-400 mt-0.5 shrink-0" />
              <p className="text-xs text-amber-300 font-mono leading-relaxed">{fallbackAlert}</p>
            </div>
          )}

          {/* Provider rows */}
          <div className="p-3 space-y-1.5">
            {providers.map((provider, idx) => {
              const cfg = STATUS_CONFIG[provider.status];
              const meta = PROVIDER_META[provider.providerId];
              return (
                <div
                  key={provider.id}
                  className="flex items-center gap-3 px-3 py-2 rounded-lg bg-[#0F172A]/60 border border-[#334155]/50 hover:border-[#334155] transition-colors"
                >
                  {/* Fallback order badge */}
                  <div
                    className="w-5 h-5 rounded flex items-center justify-center text-[9px] font-bold font-mono shrink-0"
                    style={{ background: `${meta.color}20`, color: meta.color, border: `1px solid ${meta.color}40` }}
                    title={`Fallback priority ${provider.fallbackOrder}`}
                  >
                    {provider.fallbackOrder}
                  </div>

                  {/* Provider name */}
                  <span className="text-xs font-mono text-slate-300 flex-1 truncate">{provider.name}</span>

                  {/* Latency */}
                  {provider.latency != null && provider.status === 'connected' ? (
                    <span className={`text-[10px] font-mono ${provider.latency < 100 ? 'text-emerald-400' : provider.latency < 500 ? 'text-amber-400' : 'text-red-400'}`}>
                      {provider.latency}ms
                    </span>
                  ) : (
                    <span className="text-[10px] font-mono text-slate-700">—</span>
                  )}

                  {/* Status badge */}
                  <div className={`flex items-center gap-1 px-2 py-0.5 rounded-full border text-[10px] font-mono ${cfg.badge}`}>
                    <div className={`w-1.5 h-1.5 rounded-full ${cfg.dot} ${provider.status === 'connected' ? 'animate-pulse' : ''}`} />
                    <span className={cfg.text}>{cfg.label}</span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Fallback chain indicator */}
          <div className="px-4 pb-3">
            <div className="flex items-center gap-1 flex-wrap">
              <span className="text-[10px] text-slate-600 font-mono">Fallback chain:</span>
              {providers
                .filter(p => p.fallbackOrder <= 4)
                .map((p, i, arr) => (
                  <span key={p.id} className="flex items-center gap-1">
                    <span
                      className={`text-[10px] font-mono ${p.status === 'connected' ? 'text-emerald-400' : p.status === 'unconfigured' ? 'text-slate-600' : 'text-red-400'}`}
                    >
                      {p.name.split('.')[0]}
                    </span>
                    {i < arr.length - 1 && <span className="text-slate-700 text-[10px]">→</span>}
                  </span>
                ))}
            </div>
            {lastPoll && (
              <p className="text-[10px] text-slate-700 font-mono mt-1">Last polled: {lastPoll}</p>
            )}
          </div>
        </>
      )}
    </div>
  );
}
